import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageMeta } from '@/app/shell/use-page-meta';
import {
  canAdministerPermissions,
  redirectPathForRole,
} from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import {
  PermissionCatalog,
  PermissionSetComposer,
  PermissionSetHistory,
  PermissionSetList,
  permissionCatalogQuery,
  permissionSetsQuery,
  registeredFeaturesQuery,
  DefinePermissionDialog,
} from '@/features/permissions';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/*
 * The Permissions administration page (F03 R41, R46, R51, R52), SITED WITH
 * SYSTEM CONFIGURATION under `/admin/permissions` — the admin area, alongside
 * user administration, not with billing operations. It composes the permissions
 * feature the way `admin.users` composes the users feature: a thin route
 * (loader + guard + composition, no logic) that wires the catalog, the
 * define-permission surface and the permission-set surfaces (list + composer)
 * into one page.
 *
 * F03 RECORDS; F03b REFUSES. Nothing on this route makes an authorization
 * decision: it composes the recording surfaces (the catalog, the composer, the
 * list). The way-in is withheld from a non-privileged person (the courtesy
 * half, the guard below); the server (Identity, relayed by the domain service)
 * is the authority and refuses the underlying permission/set requests
 * regardless (the rule). A build that only hides is worse than one that only
 * refuses — the server is the backstop.
 *
 *   - The way IN is withheld from anyone other than an Administrator: a
 *     `beforeLoad` guard reads the derived session from the auth store
 *     IMPERATIVELY (guards run outside React, per AGENTS.md) and, when the
 *     person does NOT hold the is_administrator privilege
 *     (`canAdministerPermissions` is false), throws a redirect that RETURNS
 *     THEM TO THEIR OWN HOME PAGE (`redirectPathForRole`). The gate is the
 *     PRIVILEGE, not a role, so an admin-ROLE account without the switch is
 *     bounced too. Permission administration gated on the privilege alone is
 *     F03b's enforcement; this guard is only the courtesy half that withholds
 *     the page (E01.F03b.R57, AC-4cc34c17).
 */
export const Route = createFileRoute('/_auth/admin/permissions')({
  /*
   * The is_administrator privilege guard (same pattern as `admin.users` and
   * `admin.account-population`): read the derived session from the auth store
   * IMPERATIVELY (getState(), never a hook — guards run outside React), and a
   * person who does not hold the privilege is RETURNED TO THEIR OWN HOME PAGE
   * via the central redirect map. The gate is the privilege, not a role. The
   * server is the authority and refuses the underlying requests regardless —
   * this only withholds the page.
   */
  beforeLoad: () => {
    const session = useAuthStore.getState().session;
    if (!canAdministerPermissions(session)) {
      throw redirect({
        to: redirectPathForRole(session?.role ?? 'client'),
      });
    }
  },
  /*
   * Prime the reads the page opens with (the catalog, the registered features
   * the define surface offers, and the permission sets) via ensureQueryData,
   * the way `invoices/` primes its first page — thin loader, no logic. The
   * screens read the same queryOptions, so the first paint has data in hand.
   */
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(permissionCatalogQuery()),
      context.queryClient.ensureQueryData(registeredFeaturesQuery()),
      context.queryClient.ensureQueryData(permissionSetsQuery()),
    ]),
  component: AdminPermissionsPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function AdminPermissionsPage() {
  usePageMeta({ title: 'Permissions' });

  const session = useAuthStore((s) => s.session);
  const [defineOpen, setDefineOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  /*
   * Which set the composer is open FOR. The list's per-row "Edit" hands the set
   * here, so it is always a set id while the composer is open; `undefined` is
   * only the closed initial state (creating a new set is out of scope,
   * finding 9). Opening the composer FOR a set shows its current permissions as
   * already chosen (R49, AC-cf93b725). The route holds this one piece of state
   * and stays thin — the composer does the reading and the replace.
   */
  const [composerSetId, setComposerSetId] = useState<string | undefined>(
    undefined,
  );
  // Which set's as-of-date HISTORY is open (R48/F69, AC-b8ffb235). The list's
  // per-row "History" hands the set here; the route opens the read-only
  // point-in-time surface for it. The as-of contract itself is the CR-E01-15
  // assumption (lib/api/domains/permissions); mounting the built surface is the
  // courtesy wiring (reachability), not a claim the contract is settled.
  const [historySetId, setHistorySetId] = useState<string | null>(null);

  return (
    <Page>
      <PageHeader
        title="Permissions"
        description="The permission catalog and the permission sets composed from it."
        actions={
          /*
           * The way in to defining a permission — a UX gate on the privilege
           * (courtesy half, E01.F03b.R57 AC-82f1631a); the server is the
           * authority and refuses the define regardless (lib/permissions, never
           * a role-string compare).
           */
          canAdministerPermissions(session) ? (
            <Button onClick={() => setDefineOpen(true)}>
              <Plus className="size-4" />
              Define permission
            </Button>
          ) : null
        }
      />

      {/* Two surfaces of the one feature: the catalog (every permission as
          record-kind + action, with its description where present) and the
          permission sets composed from it. Tabs keep both reachable from the one
          Permissions page without leaving it. */}
      <Section>
        <Tabs defaultValue="catalog">
          <TabsList>
            <TabsTrigger value="catalog">Permissions</TabsTrigger>
            <TabsTrigger value="sets">Permission Sets</TabsTrigger>
          </TabsList>

          {/* The permission catalog (R41): each permission as record-kind +
              action, its description shown where present (AC-51f2752f,
              AC-3ce30755). A define appends here (mutate → invalidate). */}
          <TabsContent value="catalog">
            <PermissionCatalog />
          </TabsContent>

          {/* The permission sets (R46, R51, R52): the list of the four fixed
              sets with their contents, and a per-row way in to EDITING a set's
              membership. Creating a new set is out of scope (finding 9) — the
              four sets are Identity-seeded (V049, is_system) and name/
              description are Identity-owned, so R46's tpa half is membership
              editing only (AC-200ea95b). There is no new-set affordance. The
              composer submits the whole membership as the replace the domain
              path forwards to Identity. */}
          <TabsContent value="sets" className="flex flex-col gap-4">
            {/* The set list, with a per-row way-in to the composer FOR that set
                (R49): the list hands the set back and the route opens the
                composer with its id so the set's current permissions show as
                already chosen (AC-cf93b725). The way-in is gated on the privilege
                (courtesy half); the server is the authority. */}
            <PermissionSetList
              onCompose={
                canAdministerPermissions(session)
                  ? (set) => {
                      setComposerSetId(set.id);
                      setComposerOpen(true);
                    }
                  : undefined
              }
              onHistory={
                canAdministerPermissions(session)
                  ? (set) => setHistorySetId(set.id)
                  : undefined
              }
            />
          </TabsContent>
        </Tabs>
      </Section>

      {/* The define-permission surface (R41): the record-kind chosen from the
          registered features, the action free text, the description optional and
          shown when present. On a successful define the catalog is invalidated
          so the new permission appears. */}
      <DefinePermissionDialog open={defineOpen} onOpenChange={setDefineOpen} />

      {/* The permission-set composer (R46, R49): available permissions grouped
          under the record kind they govern, the whole selection submitted as the
          replace. Opened FOR a set (`setId`) its current permissions show as
          already chosen (AC-cf93b725). The `key` ties the composer's internal
          selection to the set it is open for, so opening a different set
          re-seeds from THAT set's current membership rather than carrying the
          previous one. */}
      <PermissionSetComposer
        key={composerSetId ?? 'new'}
        open={composerOpen}
        onOpenChange={setComposerOpen}
        setId={composerSetId}
      />

      {/* The as-of-date history for a set (R48/F69, AC-b8ffb235), opened from a
          row's "History". Read-only; the as-of read rests on the CR-E01-15
          assumption (surface reachable now, contract confirmed later). */}
      <Dialog
        open={historySetId !== null}
        onOpenChange={(next) => {
          if (!next) setHistorySetId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permission set history</DialogTitle>
          </DialogHeader>
          {historySetId ? <PermissionSetHistory setId={historySetId} /> : null}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
